import React from 'react';
import { LightbulbIcon, MapIcon, MessageSquareTextIcon, QrCodeIcon, UsersIcon } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { easeEnter, revealTransition, stagger, viewportOnce } from '../utils/motion';
const metrics = [{
  label: 'Campaign reach',
  value: '84.2K',
  change: '+22%'
}, {
  label: 'QR redemptions',
  value: '12.4K',
  change: '+14%'
}, {
  label: 'Trial intent',
  value: '68%',
  change: '+9 pts'
}];
const bars = [24, 36, 29, 48, 53, 41, 68, 75, 61, 89, 72, 94];
export function Dashboard() {
  const reduceMotion = useReducedMotion();
  return <section id="evidence" className="bg-ink px-5 py-24 text-paper md:px-10 md:py-32" aria-labelledby="evidence-heading" data-id="element-328" data-mp="3qayj8wnv4xq3">
      <div className="mx-auto max-w-shell" data-id="element-329" data-mp="2etyugb0q510t">
        <div className="grid gap-8 md:grid-cols-2 md:items-end" data-id="element-330" data-mp="2ev072jxe1vkn">
          <div data-id="element-331" data-mp="29tkiihw056xi">
            <p className="text-xs font-extrabold tracking-[0.16em] text-paper/50" data-id="element-332" data-mp="29t2fn2eu3ugm">
              THE EVIDENCE LAYER
            </p>
            <h2 id="evidence-heading" className="mt-5 font-display text-4xl font-bold leading-[0.9] md:text-6xl" data-id="element-333" data-mp="1qt0t2297jzqc">
              Every activation leaves evidence.
            </h2>
          </div>
          <p className="max-w-xl text-lg leading-relaxed text-paper/70" data-id="element-334" data-mp="1luz9zhbs80ev">
            Move from campaign reporting to campaign intelligence. See the
            relationship between attention, real product trial, and what
            consumers choose next.
          </p>
        </div>

        <motion.div initial={reduceMotion ? false : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.15
      }} transition={revealTransition} className="mt-14 border border-paper/20" data-id="element-335" data-mp="y6g232wn9pnb">
          <div className="flex flex-col gap-4 border-b border-paper/15 px-5 py-5 md:flex-row md:items-center md:justify-between md:px-7" data-id="element-336" data-mp="2vv33httmbbiq">
            <div data-id="element-337" data-mp="2m3p6ptt1wpta">
              <div className="flex items-center gap-2.5" data-id="element-338" data-mp="2m374mgjpjhdg">
                <span className="h-2 w-2 rounded-full bg-signal" data-id="element-339" data-mp="2m1m6kgy6pgau" />
                <p className="text-sm font-extrabold" data-id="element-340" data-mp="2h6gndpw62fqg">
                  Summer discovery activation
                </p>
              </div>
              <p className="mt-1.5 text-xs font-bold text-paper/50" data-id="element-341" data-mp="39tjjkbddeedj">
                Example campaign signal · illustrative data
              </p>
            </div>
            <span className="w-fit border border-paper/25 px-3 py-1.5 text-xs font-extrabold tracking-[0.1em] text-paper/70" data-id="element-342" data-mp="34svlj5qq4x9t">
              LIVE CAMPAIGN VIEW
            </span>
          </div>

          <div className="grid gap-5 p-5 md:p-7 lg:grid-cols-[1.3fr_0.7fr]" data-id="element-343" data-mp="1tguqj939oclm">
            <div className="space-y-5" data-id="element-344" data-mp="1pcmxx0touci4">
              <div className="grid gap-3 sm:grid-cols-3" data-id="element-345" data-mp="1pfsq4oxm5ui2">
                {metrics.map((metric, index) => <motion.div key={metric.label} initial={reduceMotion ? false : {
                opacity: 0,
                y: 10
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={viewportOnce} transition={{
                duration: 0.3,
                ease: easeEnter,
                delay: stagger(index, reduceMotion)
              }} className="border border-paper/15 p-5" data-id="element-346" data-mp="4tubvsr0z9zj" data-mpi={index}>
                    <p className="text-sm text-paper/60" data-id="element-347" data-mp="2yfhugm82jno7">{metric.label}</p>
                    <p className="mt-3 font-display text-3xl font-bold" data-id="element-348" data-mp="33crjw1vgjzc1">
                      {metric.value}
                    </p>
                    <p className="mt-2 text-xs font-extrabold text-signal" data-id="element-349" data-mp="281jix66hechc">
                      {metric.change}{' '}
                      <span className="font-bold text-paper/45" data-id="element-350" data-mp="236470js1aqnk">
                        this activation
                      </span>
                    </p>
                  </motion.div>)}
              </div>

              <div className="border border-paper/15 p-5" data-id="element-351" data-mp="1pcd9yavhoz8q">
                <div className="flex items-start justify-between gap-4" data-id="element-352" data-mp="1pcwgywjlq37z">
                  <div data-id="element-353" data-mp="1kel5yihklmo6">
                    <p className="text-sm font-extrabold" data-id="element-354" data-mp="1kfd4a12jxyi5">
                      Media loop response
                    </p>
                    <p className="mt-1.5 text-xs text-paper/50" data-id="element-355" data-mp="1z495pfa3sd9s">
                      Attention converting into trial, over time
                    </p>
                  </div>
                  <span className="shrink-0 text-xs font-extrabold text-paper/50" data-id="element-356" data-mp="16karctzaf8vu">
                    Example view
                  </span>
                </div>
                <div className="mt-8 flex h-48 items-end gap-2" data-id="element-357" data-mp="3da4ce2u7iloq">
                  {bars.map((height, index) => <div key={index} className="flex-1" style={{
                  height: `${height}%`
                }} data-id="element-358" data-mp="3i1w26b5jgndt" data-mpi={index}>
                      <motion.div initial={reduceMotion ? false : {
                    scaleY: 0
                  }} whileInView={{
                    scaleY: 1
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.3,
                    ease: easeEnter,
                    delay: reduceMotion ? 0 : 0.1 + index * 0.03
                  }} style={{
                    transformOrigin: 'bottom'
                  }} className="h-full w-full bg-signal" data-id="element-359" data-mp="38987bly4cthq" />
                    </div>)}
                </div>
                <div className="mt-3 flex justify-between text-[10px] font-extrabold uppercase tracking-[0.1em] text-paper/40" data-id="element-360" data-mp="7dz02p2mc4bk">
                  <span data-id="element-361" data-mp="3eq73mfekah2">Attention</span>
                  <span data-id="element-362" data-mp="3ujzvj9nr0gok">Participation</span>
                  <span data-id="element-363" data-mp="3ui57vnl3eags">Trial</span>
                  <span data-id="element-364" data-mp="3pkn6qjdbgzjc">Signal</span>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2" data-id="element-365" data-mp="3pmpusxuwsilf">
                <SmallEvidence icon={<UsersIcon size={17} aria-hidden="true" data-id="element-367" data-mp="3kndbuo75rvo4" />} title="Audience detail" detail="25–34 led trial intent in city-centre activations." data-id="element-366" data-mp="3kmlou1rmn69g" />
                <SmallEvidence icon={<MessageSquareTextIcon size={17} aria-hidden="true" data-id="element-369" data-mp="1rbfvd11t4ub4" />} title="Sentiment signal" detail="“Would purchase” appeared in 68% of feedback." data-id="element-368" data-mp="1iuyx1xzwv7bx" />
              </div>
            </div>

            <aside className="space-y-5" data-id="element-370" data-mp="2041fakh6d71j">
              <div className="border border-signal/45 p-5" data-id="element-371" data-mp="252l0giny2esu">
                <span className="grid h-10 w-10 place-items-center bg-signal text-white" data-id="element-372" data-mp="2522z57mfdae2">
                  <LightbulbIcon size={19} aria-hidden="true" data-id="element-373" data-mp="18dn3ctsq24si" />
                </span>
                <p className="mt-6 text-xs font-extrabold uppercase tracking-[0.12em] text-signal" data-id="element-374" data-mp="1ceymqhl05ts9">
                  Illustrative recommendation
                </p>
                <h3 className="mt-3 font-display text-2xl font-bold leading-tight" data-id="element-375" data-mp="1mbtm12p3ny7l">
                  Shift the next drop closer to campus.
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-paper/70" data-id="element-376" data-mp="2srxa1jn6oovo">
                  Trial intent was stronger near university locations. Test a
                  larger allocation there in the next activation window.
                </p>
              </div>
              <div className="border border-paper/15 p-5" data-id="element-377" data-mp="3euqppdwkwlof">
                <p className="text-sm font-extrabold" data-id="element-378" data-mp="3sl3w3bttnsbn">Evidence captured</p>
                <ul className="mt-5 space-y-4 text-sm text-paper/70" data-id="element-379" data-mp="3smf3dlmh7i7i">
                  <li className="flex items-center gap-3" data-id="element-380" data-mp="3ntlviovpntr7">
                    <QrCodeIcon size={17} className="shrink-0 text-signal" aria-hidden="true" data-id="element-381" data-mp="6dnjax5jt8bt" />
                    Redeemed product trial
                  </li>
                  <li className="flex items-center gap-3" data-id="element-382" data-mp="fdaj5eimq62r">
                    <MapIcon size={17} className="shrink-0 text-signal" aria-hidden="true" data-id="element-383" data-mp="aezc1bjpy9o8" />
                    Location performance
                  </li>
                  <li className="flex items-center gap-3" data-id="element-384" data-mp="2c8f2o8ft3oj7">
                    <MessageSquareTextIcon size={17} className="shrink-0 text-signal" aria-hidden="true" data-id="element-385" data-mp="robdn2riviak" />
                    Feedback and purchase intent
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </motion.div>
      </div>
    </section>;
}
function SmallEvidence({
  icon,
  title,
  detail
}: {
  icon: React.ReactNode;
  title: string;
  detail: string;
}) {
  return <div className="flex gap-3 border border-paper/15 p-4" data-id="element-386" data-mp="dyg8kfvstg24">
      <span className="grid h-9 w-9 shrink-0 place-items-center border border-paper/20 text-paper/80" data-id="element-387" data-mp="3w31fgyfjl3rz">
        {icon}
      </span>
      <div data-id="element-388" data-mp="3yh34ruoe5th">
        <p className="text-sm font-extrabold" data-id="element-389" data-mp="400hue0oo8t5">{title}</p>
        <p className="mt-1 text-xs leading-relaxed text-paper/55" data-id="element-390" data-mp="1ubojwzzgs88l">{detail}</p>
      </div>
    </div>;
}